import dayjs from "apps/visits/packages/shared/libs/dayjs";
import { getDateOfBirthSchema, getPeselSchema } from "apps/visits/packages/shared/validators";
import type { TFunction } from "i18next";

// Patients sign up for themselves; a minor would need a legal guardian, and no flow supports that.
export const ADULT_AGE = 18;

// PESEL encodes the century in the month field: the month is shifted by a per-century offset.
//   01–12 → 1900s, 21–32 → 2000s, 41–52 → 2100s, 61–72 → 2200s, 81–92 → 1800s.
const CENTURY_RANGES = [
  { monthOffset: 0, century: 1900 },
  { monthOffset: 20, century: 2000 },
  { monthOffset: 40, century: 2100 },
  { monthOffset: 60, century: 2200 },
  { monthOffset: 80, century: 1800 },
] as const;

const ISO_DATE_FORMAT = "YYYY-MM-DD";

const pad = (value: number) => String(value).padStart(2, "0");

/**
 * Decodes the full date of birth from a PESEL. Returns null when the number is malformed or when
 * the encoded date does not exist (month 13, 31 February, 29 February outside a leap year) — the
 * caller decides whether that means "invalid PESEL" or simply "no date available".
 */
export const extractDateOfBirthFromPesel = (pesel: string | undefined | null): Date | null => {
  if (!pesel || !/^\d{11}$/.test(pesel)) return null;

  const encodedMonth = Number(pesel.substring(2, 4));
  const range = CENTURY_RANGES.find(
    ({ monthOffset }) => encodedMonth > monthOffset && encodedMonth <= monthOffset + 12,
  );
  if (!range) return null;

  const year = range.century + Number(pesel.substring(0, 2));
  const month = encodedMonth - range.monthOffset;
  const day = Number(pesel.substring(4, 6));

  // Strict parsing rejects overflowing days ("1905-02-31" would otherwise roll over to March.)
  const birthDate = dayjs(`${year}-${pad(month)}-${pad(day)}`, ISO_DATE_FORMAT, true);

  return birthDate.isValid() ? birthDate.toDate() : null;
};

/**
 * Age is counted from the full date, so someone turning 18 today passes and someone turning 18
 * tomorrow does not — dayjs `diff` in years truncates towards zero, which is exactly that rule.
 */
export const isAdultBirthDate = (date: Date | undefined | null): boolean => {
  if (!date) return false;

  const birthDate = dayjs(date);
  if (!birthDate.isValid()) return false;

  return dayjs().diff(birthDate, "year") >= ADULT_AGE;
};

/**
 * PESEL field whose encoded date of birth has to be a real one — 11 digits alone are not enough to
 * make a PESEL real. Says nothing about age, so it fits the flows where a minor patient is allowed.
 * An absent value is left to the surrounding `required` rule.
 */
export const getPeselWithValidBirthDateSchema = (t: TFunction) =>
  getPeselSchema(t).test("pesel-birth-date", t("COMMONS.ERRORS.INCORRECT_PESEL"), (value) =>
    value ? extractDateOfBirthFromPesel(value) !== null : true,
  );

/**
 * The same, narrowed to adults. An undecodable value passes this test on purpose — reporting it as
 * "minor" would be wrong, and the `pesel-birth-date` test above already rejects it as malformed.
 */
export const getAdultPeselSchema = (t: TFunction) =>
  getPeselWithValidBirthDateSchema(t).test("adult-only", t("COMMONS.ERRORS.ADULT_ONLY"), (value) => {
    const birthDate = extractDateOfBirthFromPesel(value);

    return birthDate ? isAdultBirthDate(birthDate) : true;
  });

/** Date-of-birth field limited to adults; `dateFormat` defaults to the app-wide `DD.MM.YYYY`. */
export const getAdultDateOfBirthSchema = (t: TFunction, dateFormat?: string) =>
  getDateOfBirthSchema(t, dateFormat).test("adult-only", t("COMMONS.ERRORS.ADULT_ONLY"), (value) =>
    value ? isAdultBirthDate(value) : true,
  );
